/**
 * User profile page component.
 */
import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { getGravatarUrl } from '../utils/gravatar';
import { Layout } from '../components/Layout';
import './ProfilePage.css';

export const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { isConnected, on, off } = useWebSocket();

  // Register WebSocket event handlers for user updates
  useEffect(() => {
    const handleUserUpdate = async (message: { event: string; data?: any }) => {
      const { event_type, user_id, user_data } = message.data || {};
      
      // Only handle updates for the current user
      if (user && user_id === user.id && event_type === 'updated') {
        // Refresh user data from context
        await refreshUser();
      }
    };

    const unsubscribe = on('user_update', handleUserUpdate);

    return () => {
      unsubscribe();
    };
  }, [user, on, off, refreshUser]);

  if (!user) {
    return (
      <Layout>
        <div className="profile-page">
          <div className="profile-container">
            <div className="loading-message">Loading profile...</div>
          </div>
        </div>
      </Layout>
    );
  }

  const gravatarUrl = user.email
    ? getGravatarUrl(user.email, 200, 'initials', user.name)
    : '';

  const createdDate = user.created_at ? new Date(user.created_at) : null;
  const formattedCreatedDate = createdDate
    ? createdDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    : 'Unknown';

  return (
    <Layout>
      <div className="profile-page">
        <div className="profile-container">
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-avatar">
                {gravatarUrl ? (
                  <img
                    src={gravatarUrl}
                    alt={user.name || user.email || 'User avatar'}
                    className="profile-avatar-img"
                  />
                ) : (
                  <span className="profile-avatar-initials">
                    {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              <div className="profile-header-info">
                <h1 className="profile-username">{user.name || user.email}</h1>
                <div className="profile-email">{user.email}</div>
                <div className="profile-created">Created: {formattedCreatedDate}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

